import React, { ReactNode, useContext, useEffect, useState } from 'react';

import DeviceSyncMergesContext from '@totem/components/administration/deviceSyncMerges/DeviceSyncMergesContext';
import {
  SyncConflict,
  SyncConflictResults,
} from '@totem/components/administration/deviceSyncMerges/types';
import InputContext from '@totem/components/ui_components/reportSettings/InputContext';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { Params } from '@totem/types/common';
import { DEVICE_SYNC_CONFLICTS_ENDPOINT } from '@totem/utilities/endpoints';
import { sortStringAscending } from '@totem/utilities/tableUtilities';

type Props = {
  children?: ReactNode;
};

const DeviceSyncMergesContainer = ({ children }: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const {
    buildParameters,
    setFilterOptions,
    filterUpdateCount,
    sortingUpdateCount,
    pagingUpdateCount,
    isReady,
  } = useContext(InputContext);

  const [loading, setLoading] = useState<boolean>(true);
  const [conflicts, setConflicts] = useState<SyncConflict[]>([]);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [refreshData, setRefreshData] = useState<boolean>(true);

  // Conflicts span every organization, so the options are the user's full organization list.
  useEffect(() => {
    const orgOptions = [...(userProfile.organizations ?? [])]
      .sort((chkA, chkB) => sortStringAscending(chkA.name, chkB.name))
      .map((org) => ({ text: org.name, value: org.id }));

    setFilterOptions('organization', orgOptions);
  }, [userProfile.organizations]);

  useEffect(() => {
    if (isReady) {
      setRefreshData(true);
    }
  }, [isReady, filterUpdateCount, sortingUpdateCount, pagingUpdateCount]);

  useEffect(() => {
    if (!isReady || !refreshData) {
      return;
    }

    setLoading(true);

    const params: Params = buildParameters();

    fetch(DEVICE_SYNC_CONFLICTS_ENDPOINT, {
      method: 'POST',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(params),
    })
      .then((res) => res.json())
      .then((result: SyncConflictResults) => {
        setConflicts(result?.conflicts ?? []);

        setTotalRecords(result?.paging?.totalRecords ?? 0);
      })
      .finally(() => {
        setLoading(false);
      });

    setRefreshData(false);
  }, [refreshData, isReady, getApiToken]);

  return (
    <DeviceSyncMergesContext.Provider
      value={{
        loading,
        conflicts,
        totalRecords,
        refetch: () => setRefreshData(true),
      }}
    >
      {children}
    </DeviceSyncMergesContext.Provider>
  );
};

export default DeviceSyncMergesContainer;
